import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import useLipaBidhaaStore from "../../../stores/lipaBidhaaStore";
import { useLipaBidhaaTransaction } from "../../../hooks/queries/useLipaBidhaaQueries";
import { merchantQueryKeys } from "../../../hooks/queries/useMerchantQueries";
import { TransactionQueryKeys } from "../../../hooks/queries/useTransactionQueries";
import {
  POLL_TIMEOUT_MS,
  TERMINAL_TRANSACTION_STATUSES,
} from "../config/lipaBidhaaConfig";

export function usePollTransaction() {
  const queryClient = useQueryClient();
  const { transactionUuid } = useLipaBidhaaStore(
    useShallow((state) => ({ transactionUuid: state.transactionUuid })),
  );

  const [isPolling, setIsPolling] = useState(false);
  const timeoutRef = useRef(null);

  const { data: pollData } = useLipaBidhaaTransaction(
    isPolling ? transactionUuid : null,
  );

  const status = pollData?.status;
  const pollDataRef = useRef(pollData);
  pollDataRef.current = pollData;

  useEffect(() => {
    if (!status) return;
    if (!TERMINAL_TRANSACTION_STATUSES.has(status)) return;
    clearTimeout(timeoutRef.current);
    setIsPolling(false);
    useLipaBidhaaStore.getState().setTransactionResult(pollDataRef.current);
    useLipaBidhaaStore.getState().setCurrentStep(4);
    queryClient.invalidateQueries({ queryKey: merchantQueryKeys.merchantBalance() });
    queryClient.invalidateQueries({ queryKey: TransactionQueryKeys.all });
  }, [status, queryClient]);

  useEffect(() => {
    if (!isPolling) return;
    timeoutRef.current = setTimeout(() => {
      setIsPolling(false);
      useLipaBidhaaStore.getState().setCurrentStep(4);
    }, POLL_TIMEOUT_MS);
    return () => clearTimeout(timeoutRef.current);
  }, [isPolling]);

  const startPolling = useCallback(() => {
    if (!useLipaBidhaaStore.getState().transactionUuid) return;
    setIsPolling(true);
  }, []);

  return { isPolling, startPolling };
}
